import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * Quantas cápsulas estão na estufa — o número do selo da barra de baixo
 * (onda 308, spec 06 §5.2, decisão D1).
 *
 * O laboratorista tira as cápsulas da estufa de manhã, pesa, e durante o dia
 * põe outras. O número no ícone da Estufa diz, sem abrir a tela, se há
 * pesagem seca esperando por ele.
 *
 * ── Quando relê ───────────────────────────────────────────────────────
 *
 *  · a cada 60 s, enquanto a aba está à vista (o TanStack não relê com a aba
 *    escondida — a bateria do celular agradece);
 *  · ao voltar para a aba (`refetchOnWindowFocus`), se o número tiver mais
 *    de 30 s;
 *  · a cada troca de tela — quem acabou de pesar na Estufa e toca em outro
 *    atalho vê o número já descontado;
 *  · logo depois de uma pesagem: a tela que grava dispara na janela o aviso
 *    `EVENTO_DA_ESTUFA` (`labmanager:estufa-mudou`), e o número relê na hora
 *    (Ruling R11 — a Estufa da onda 310 não usa o `QueryClient`, e um aviso
 *    da janela não amarra quem grava a quem conta). Invalidar a chave
 *    `CHAVE_DA_CONTAGEM` (`['estufa', 'contagem']`) no `QueryClient` do
 *    `main.tsx` também serve.
 *
 * ── Quando NÃO pergunta ───────────────────────────────────────────────
 *
 * Com `ativo` falso: fora do celular, sem a Estufa na barra ou sem
 * `ensaio.ver` (a Moldura decide). A API recusaria com 403 quem não tem a
 * permissão, e uma recusa a cada minuto no console de quem nem vê a bancada
 * seria barulho. O `refetch` da troca de tela tem a mesma guarda: no TanStack
 * ele busca mesmo com `enabled: false`.
 *
 * Sem `retry`: a próxima leitura já vem em um minuto, e uma falha de rede não
 * apaga o número — o TanStack mantém o último lido até chegar outro.
 */
export const CHAVE_DA_CONTAGEM = ['estufa', 'contagem'] as const;

/**
 * O aviso que a Estufa dispara na janela depois de gravar uma pesagem seca:
 * `window.dispatchEvent(new CustomEvent(EVENTO_DA_ESTUFA))`.
 */
export const EVENTO_DA_ESTUFA = 'labmanager:estufa-mudou';

export function useContagemDaEstufa(ativo: boolean): number | null {
  const { pathname } = useLocation();
  const { data, refetch } = useQuery({
    queryKey: CHAVE_DA_CONTAGEM,
    queryFn: ({ signal }) => api<{ naEstufa: number }>('/estufa/contagem', { sinal: signal }),
    enabled: ativo,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: false,
  });

  /* A troca de tela. O endereço da montagem não conta: ao montar, o
     `useQuery` já pergunta. */
  const ultimo = useRef(pathname);
  useEffect(() => {
    if (ultimo.current === pathname) return;
    ultimo.current = pathname;
    if (ativo) void refetch();
  }, [pathname, ativo, refetch]);

  /* O aviso da Estufa. Só escuta quando ativo, pela mesma razão do `refetch`
     da troca de tela: inativo, a leitura seria um 403 ou um pedido à toa. */
  useEffect(() => {
    if (!ativo) return undefined;
    const aoMudar = () => { void refetch(); };
    window.addEventListener(EVENTO_DA_ESTUFA, aoMudar);
    return () => window.removeEventListener(EVENTO_DA_ESTUFA, aoMudar);
  }, [ativo, refetch]);

  return ativo ? data?.naEstufa ?? null : null;
}
